import { css } from '@emotion/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import CollapsiblePanel, { InitialState } from 'athena/components/collapsible-panel';
import NvIcon from 'shared/components/nv-icon';
import { getNextMentorshipProgramSession } from 'redux/selectors/mentorship-program-sessions';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { getMentoringProgramSessions } from 'redux/actions/mentoring-program-sessions';
import { ConversationStarterCategory, SessionFilters } from 'redux/schemas/models/mentoring-program-sessions';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { RootState } from 'redux/schemas';
import setupMenteeLensEventsListener from 'athena/helpers/mentee-lens-channel-events';
import { genAIGradient, gray5, hexToRgbaString, lowTide } from 'styles/global_defaults/colors';
import { blue05, dunes, seafoam } from 'athena/styles/colors';
import { halfSpacing, largeSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { openSansCondensed } from 'styles/global_defaults/fonts';
import t from 'react-translate';

type Props = {
  programId: number;
  connectionId: number;
};

const CATEGORY_COLOR: Record<ConversationStarterCategory, string> = {
  explore: hexToRgbaString(seafoam, 0.4),
  connect: hexToRgbaString(lowTide, 0.4),
  challenge: hexToRgbaString(dunes, 0.4),
};

const CATEGORY_KEY: Record<ConversationStarterCategory, 'EXPLORE' | 'CONNECT' | 'CHALLENGE'> = {
  explore: 'EXPLORE',
  connect: 'CONNECT',
  challenge: 'CHALLENGE',
};

const styles = css`
  .collapsible-panel {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: ${quarterSpacing}px;
      background: linear-gradient(48.41deg, #60F3AB 0%, #00CCBC 45%, #009AC0 100%);
      border-radius: ${halfSpacing}px ${halfSpacing}px 0 0;
    }
  }

  .mentee-lens-header {
    gap: ${halfSpacing}px;

    .icon {
      background: ${genAIGradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      flex-shrink: 0;
    }
  }

  .mentee-lens-subtitle {
    white-space: normal;
    line-height: 19px;
  }

  .mentee-lens-content {
    gap: ${standardSpacing}px;
    padding: 0 ${halfSpacing}px;
  }

  .mentee-lens-summary {
    background-color: ${hexToRgbaString(lowTide, 0.3)};
    border-radius: ${quarterSpacing}px;
    padding: ${standardSpacing}px ${largeSpacing}px;
  }

  .starters-section {
    gap: ${quarterSpacing}px;
  }

  .starters-section-header {
    gap: ${halfSpacing}px;
  }

  .starters-icon-pill {
    background-color: ${blue05};
    border-radius: 100px;
    padding: ${quarterSpacing}px;

    .icon {
      background: ${genAIGradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .mentee-lens-starters {
    gap: ${halfSpacing}px;
  }

  .starter-item {
    gap: ${halfSpacing}px;
    border: 1px solid ${gray5};
    border-radius: ${quarterSpacing}px;
    padding: ${halfSpacing}px ${threeQuartersSpacing}px;

    .starter-text {
      line-height: 24px;
    }
  }

  .starter-badge {
    height: 20px;
    border-radius: 100px;
    padding: 0 ${halfSpacing}px;
    font-family: ${openSansCondensed};
  }
`;

const MenteeLensPanel = ({ programId, connectionId }: Props) => {
  const dispatch = useAppDispatch();
  const session = useSelector((state: RootState) => getNextMentorshipProgramSession(state, connectionId));
  const { MenteeTitleAlias } = useSelector((state: RootState) => getMentoringProgramAliases(state, programId));

  useEffect(() => {
    dispatch(getMentoringProgramSessions({ programId, connectionId, filter: SessionFilters.UPCOMING }));
  }, [programId, connectionId, dispatch]);

  const { id: sessionId, preparationNote, conversationStarters, mentorshipProgramConnection } = session || {};

  useEffect(() => {
    if (!sessionId) {
      return () => {};
    }

    const cleanup = setupMenteeLensEventsListener(sessionId, dispatch);
    return () => {
      cleanup();
    };
  }, [sessionId, dispatch]);

  if (!preparationNote) return null;

  const menteeFirstName = mentorshipProgramConnection?.mentorshipProgramConnectionMembers
    ?.find((m) => m.mentorshipProgramEnrollment.role.name === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE)
    ?.mentorshipProgramEnrollment?.user?.firstName;

  const panelTitle = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTEE_LENS.PANEL_TITLE({ menteeTitleAlias: MenteeTitleAlias ?? '' });

  const title = (
    <div className='mentee-lens-header d-flex align-items-center'>
      <NvIcon icon='gen-ai' size='small' />
      <div>
        <div className='heading-5'>{panelTitle}</div>
        {menteeFirstName && (
          <p className='mentee-lens-subtitle text-small font-weight-normal black m-0'>
            {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTEE_LENS.PANEL_SUBTITLE(menteeFirstName)}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div css={styles}>
      <CollapsiblePanel
        title={title}
        panelState={{ initialState: InitialState.COLLAPSED }}
        ariaLabel={panelTitle}
      >
        <div className='mentee-lens-content d-flex flex-column'>
          <div className='mentee-lens-summary text-body mt-4'>
            {preparationNote}
          </div>
          {conversationStarters?.length > 0 && (
            <div className='starters-section d-flex flex-column'>
              <div className='starters-section-header d-flex align-items-center'>
                <div className='starters-icon-pill d-flex align-items-center'>
                  <NvIcon icon='conversations' size='smallest' />
                </div>
                <span className='heading-5'>
                  {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTEE_LENS.STARTERS_HEADER()}
                </span>
              </div>
              <div className='mentee-lens-starters d-flex flex-column'>
                {conversationStarters.map((starter) => (
                  <div key={starter.conversationStarter} className='starter-item d-flex align-items-center'>
                    <span
                      className='starter-badge d-flex align-items-center justify-content-center flex-shrink-0 font-weight-bold text-nowrap text-small'
                      style={{ backgroundColor: CATEGORY_COLOR[starter.category] }}
                    >
                      {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTEE_LENS.CATEGORY[CATEGORY_KEY[starter.category]]()}
                    </span>
                    <span className='starter-text text-medium'>
                      {starter.conversationStarter}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsiblePanel>
    </div>
  );
};

export default MenteeLensPanel;
